import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles } from "@material-ui/core/styles";

import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Collapse from '@material-ui/core/Collapse';
import Alert from '@material-ui/lab/Alert';

import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBackIos';

import ClubListItem from 'app/components/club/ClubListItem';
import ClubAvatar from 'app/components/club/ClubAvatar';
import Link from 'app/components/Link';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    container: {
        maxWidth: 420
    },
    list: {
        minHeight: '40vh',
        maxHeight: '60vh',
        overflowY: 'scroll',
        marginBottom: theme.spacing(3),
        boxShadow: '0px -5px 10px -8px inset'
    },
    notation: {
        marginTop: theme.spacing(3),
        textAlign: 'center'
    },
    avatarContainer: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    }
}));

export default function FindClubForm (props) {

    const { onLoading } = props;

    const [ step, setStep ] = useState(1);

    const [ clubs, setClubs ] = useState([]);
    const [ filteredClubs, setFilteredClubs ] = useState([]);

    const [ searchValue, setSearchValue ] = useState("");

    const [ selectedClub, setSelectedClub ] = useState(null);
    const [ requestNotes, setRequestNotes ] = useState("");
    const [ accessExpanded, setAccessExpanded ] = useState(false);
    const [ requestSuccessful, setRequestSuccessful ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState(false);

    const classes = useStyles();
    const api = useApi();

    useEffect(() => {
        getClubs();
    }, [])

    const getClubs = () => {
        onLoading(true);

        api.get('/club')
            .then(res => {
                setClubs(res.clubs)
                setFilteredClubs(res.clubs);
            }).then(() => onLoading(false))
    }

    // Setter Functions ------------------------------------------

    const handleSearchValue = event => {
        setSearchValue(event.target.value);
    }

    const handleSearch = (event, bypass = false) => {

        if (bypass || event.key === 'Enter') {

            event.preventDefault();

            let filteredOptions = clubs.filter((c) => c.name.toLowerCase().includes(searchValue.toLowerCase()));

            setFilteredClubs(filteredOptions);
        }
    };

    const handleClearSearch = () => {
        setSearchValue("");
        setFilteredClubs([...clubs]);
    }

    const handleMouseDown = (event) => {
        event.preventDefault();
    };

    // View Club Functions
    const handleClubSelect = club => {
        setSelectedClub(club);
        setStep(club ? 2 : 1);
        setAccessExpanded(false);
        setErrorMessage(false);
        setRequestSuccessful(false);
    }

    const expandRequestAccess = () => {
        setAccessExpanded(true);
    }

    const handleRequestNotes = event => {
        setRequestNotes(event.target.value)
    }

    // Poster Functions ------------------------------------------

    const postRequestAccess = () => {
        onLoading(true);
        setErrorMessage(false);
        setRequestSuccessful(false);

        const data = {
            club_id: selectedClub.id,
            notes: requestNotes
        }

        api.post('/club/request-access', data)
            .then(() => setRequestSuccessful(true))
            .catch(error => {
                if (error.response.data && error.response.data.error) {
                    setErrorMessage(error.response.data.error)
                } else {
                    setErrorMessage('An unknown error has occured. Please try again later.')
                }
            }).then(() => onLoading(false))
    }

    return (
        <div className={classes.container}>
            { (step === 1) &&
                <Fragment>
                    <Typography gutterBottom variant="body1" align="center" color="textPrimary">
                        Find a publicly searchable club.
                    </Typography>
                    <FormControl variant="outlined" fullWidth margin="normal">
                        <OutlinedInput
                            id="input-club-search"
                            value={searchValue}
                            onChange={handleSearchValue}
                            onKeyPress={handleSearch}
                            placeholder="Search for a club..."
                            color="secondary"
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Search"
                                        onClick={(event) => handleSearch(event, true)}
                                        onMouseDown={handleMouseDown}
                                        edge="end"
                                        >
                                        <SearchIcon/>
                                    </IconButton>
                                    <IconButton
                                        aria-label="Clear Search Field"
                                        onClick={handleClearSearch}
                                        onMouseDown={handleMouseDown}
                                        edge="end"
                                        >
                                        <CloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                    <List className={classes.list}>
                        {filteredClubs.length ? filteredClubs.map(club => <ClubListItem key={club.id} club={club} divider={true} button onClick={() => handleClubSelect(club)} />) : (
                            <Typography variant="subtitle2" align="center" color="textSecondary" component="li" className={classes.marginTop}>
                                No clubs match your search. Please try again.
                            </Typography>
                        )}
                    </List>
                    <Typography gutterBottom variant="subtitle2" align="center" color="textSecondary">
                        *If you can not find the club you are looking for, please contact the club's administrator as most of our clubs are not publicly searchable.
                    </Typography>
                    <div className={classes.notation}>
                        <Link to="/register/club/new" color="primary" route>
                            Create a New Club
                        </Link>
                    </div>
                </Fragment>
            }
            { (step === 2) &&
                <Fragment>
                    <Button
                        startIcon={<LeftIcon/>}
                        aria-label="Back"
                        onClick={() => handleClubSelect(null)}
                        >
                        Back to Search
                    </Button>
                    <div className={classes.avatarContainer}>
                        <ClubAvatar club={selectedClub} />
                    </div>
                    <Typography variant="h5" color="textPrimary" gutterBottom>{selectedClub.name}</Typography>
                    <Typography gutterBottom>{selectedClub.description}</Typography>
                    <Typography color="primary" variant="subtitle2">{selectedClub.users_count} Total Users</Typography>
                    <Collapse in={accessExpanded} className={classes.marginTop}>
                        <TextField
                            value={requestNotes}
                            onChange={handleRequestNotes}
                            multiline
                            rows={4}
                            placeholder="Add a few notes to help the club administrator recognize your profile."
                            variant="outlined"
                            fullWidth
                        />
                    </Collapse>
                    <Button
                        className={classes.marginTop}
                        variant="contained"
                        color="primary"
                        aria-label={accessExpanded ? 'Send Request' : 'Request Access'}
                        onClick={accessExpanded ? postRequestAccess : expandRequestAccess}
                        fullWidth
                        >
                        {accessExpanded ? 'Send Request' : 'Request Access'}
                    </Button>
                    { (errorMessage) &&
                        <Alert severity='error' className={classes.marginTop}>{errorMessage}</Alert>
                    }
                    { (requestSuccessful) &&
                        <Alert severity='success' className={classes.marginTop}>
                            Your request for access has been submitted! We will email you once your request has been resolved. You can view the status of your request on your <Link to="/profile/clubs" color="primary" route>profile page under clubs</Link>.
                        </Alert>
                    }
                </Fragment>
            }
        </div>
    );
}
